import React, { useEffect, useMemo, useRef, useState } from "react";
import { Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import OnboardingStepper from "../components/onboarding/Stepper";
import OnboardingCard from "../components/onboarding/OnboardingCard";
import CompanyStep from "../components/onboarding/steps/CompanyStep";
import AddressStep from "../components/onboarding/steps/AddressStep";
import IdentityStep from "../components/onboarding/steps/IdentityStep";
import ContactStep from "../components/onboarding/steps/ContactStep";
import PreferencesStep from "../components/onboarding/steps/PreferencesStep";

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

const STEP_DEFS = [
  { key: "company", title: "Informations générales", description: "Le secteur d'activité de votre entreprise.", icon: "Building2" },
  { key: "address", title: "Coordonnées", description: "Adresse et informations administratives.", icon: "MapPin" },
  { key: "identity", title: "Identité visuelle", description: "Votre logo (optionnel).", icon: "Palette" },
  { key: "contact", title: "Contact principal", description: "La personne référente pour votre entreprise.", icon: "UserCircle" },
  { key: "preferences", title: "Préférences", description: "Comment nous devons vous contacter.", icon: "Settings" },
];

export default function OnboardingWizard() {
  const { setUser } = useAuth();
  const [status, setStatus] = useState(null);
  const [agencyContact, setAgencyContact] = useState(null);
  const [formData, setFormData] = useState({ company: {}, address: {}, identity: {}, contact: {}, preferences: {} });
  const [activeIndex, setActiveIndex] = useState(0);
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [announcement, setAnnouncement] = useState("");
  const [justCompleted, setJustCompleted] = useState(null);
  // Étape la plus loin atteinte : une étape facultative laissée vide avant elle a été passée, pas verrouillée.
  const [furthest, setFurthest] = useState(0);
  useEffect(() => { setFurthest((f) => Math.max(f, activeIndex)); }, [activeIndex]);
  const firstFieldRef = useRef(null);

  const load = async () => {
    const { data } = await api.get("/client/onboarding/status");
    setStatus(data);
    setFormData(data.values);
    const initial = typeof data.current_step === "number" ? data.current_step : 0;
    setActiveIndex(Math.min(Math.max(initial, 0), STEP_DEFS.length - 1));
  };

  useEffect(() => {
    load();
    api.get("/client/agency-contact").then(({ data }) => setAgencyContact(data)).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const steps = useMemo(() => {
    if (!status) return STEP_DEFS.map((s) => ({ ...s, done: false, required: false }));
    return STEP_DEFS.map((s) => {
      const st = status.steps.find((x) => x.key === s.key) || {};
      return { ...s, done: !!st.done, required: !!st.required };
    });
  }, [status]);

  const firstIncompleteIndex = steps.findIndex((s) => !s.done);
  const boundary = firstIncompleteIndex === -1 ? steps.length - 1 : firstIncompleteIndex;
  const isReachable = (i) => steps[i]?.done || i <= boundary;
  const maxReached = steps.reduce((m, _s, i) => (isReachable(i) ? i : m), 0);

  useEffect(() => {
    if (!status) return;
    firstFieldRef.current?.focus();
    setAnnouncement(`Étape ${activeIndex + 1} sur ${steps.length} : ${steps[activeIndex]?.title}`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeIndex, status]);

  const goTo = async (i) => {
    if (!isReachable(i)) return;
    setActiveIndex(i);
    try { await api.put("/client/onboarding/progress", { current_step: i }); } catch { /* non-bloquant */ }
  };

  const uploadLogo = async (file) => {
    setUploadingLogo(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/client/identity/logo", fd);
      setFormData((f) => ({ ...f, identity: { logos: [...(f.identity.logos || []), data] } }));
      toast.success("Logo ajouté");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec de l'envoi du logo");
    } finally { setUploadingLogo(false); }
  };

  const deleteLogo = async (path) => {
    try {
      await api.delete("/client/identity/logo", { params: { path } });
      setFormData((f) => ({ ...f, identity: { logos: (f.identity.logos || []).filter((l) => l.path !== path) } }));
    } catch { toast.error("Échec de la suppression"); }
  };

  const saveAndAdvance = async () => {
    const key = steps[activeIndex].key;
    const data = formData[key];

    if (key === "company" && !(data.nature || "").trim()) return toast.error("Le secteur d'activité est requis");
    if (key === "address") {
      for (const f of ["address", "postal_code", "city", "country"]) {
        if (!(data[f] || "").trim()) return toast.error("Adresse, code postal, ville et pays sont requis");
      }
    }
    if (key === "contact") {
      if (!(data.name || "").trim()) return toast.error("Le nom du contact est requis");
      if (!EMAIL_RE.test(data.email || "")) return toast.error("Adresse e-mail du contact invalide");
    }

    setSaving(true);
    try {
      if (key === "company") await api.put("/client/company-details", data);
      else if (key === "address") await api.put("/client/company-info", data);
      else if (key === "contact") await api.put("/client/contacts/primary", data);
      else if (key === "preferences") await api.put("/client/preferences", data);
      // identity: rien à sauvegarder (upload/suppression déjà immédiats)

      if (activeIndex === steps.length - 1) {
        const { data: freshUser } = await api.post("/client/onboarding/complete");
        setJustCompleted(freshUser);
        setTimeout(() => setUser(freshUser), 1800);
        return;
      }
      await load();
      setActiveIndex((i) => Math.min(i + 1, steps.length - 1));
      await api.put("/client/onboarding/progress", { current_step: Math.min(activeIndex + 1, steps.length - 1) });
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Erreur lors de l'enregistrement");
    } finally { setSaving(false); }
  };

  if (justCompleted) {
    return (
      <div className="min-h-screen flex items-center justify-center p-8 beige-texture">
        <div className="w-full max-w-sm bg-sable rounded-2xl border border-marine/10 shadow-md p-10 text-center" data-testid="onboarding-confirmation">
          <CheckCircle2 size={16} className="text-bleu mx-auto mb-4" aria-hidden="true" />
          <h1 className="font-display text-2xl text-marine mb-2">Bienvenue !</h1>
          <p className="text-sm text-marine/80">Votre espace est prêt. Redirection vers votre tableau de bord…</p>
        </div>
      </div>
    );
  }

  if (!status) {
    return (
      <div className="min-h-screen flex items-center justify-center beige-texture">
        <Loader2 className="animate-spin text-marine" size={32} />
      </div>
    );
  }

  const isLast = activeIndex === steps.length - 1;

  return (
    <div className="min-h-screen p-6 sm:p-10 beige-texture flex justify-center">
      <div className="w-full max-w-xl">
        <div className="text-xs uppercase tracking-[0.25em] font-semibold text-bordeaux mb-2">Bienvenue chez Metara</div>
        <h1 className="font-display text-3xl text-marine mb-2">Configurons votre espace</h1>
        <p className="text-sm text-marine/80 mb-6">Quelques informations essentielles avant d'accéder à votre portail.</p>

        <div aria-live="polite" data-testid="onboarding-announcement" className="sr-only">{announcement}</div>

        <div className="flex items-center justify-between mb-2">
          <OnboardingStepper steps={steps} current={activeIndex} maxReached={maxReached} onJump={goTo} />
          <span className="text-xs text-marine/80 shrink-0 ml-3">{status.progress.percent}%</span>
        </div>
        <div className="h-1.5 rounded-full bg-marine/10 mb-6 overflow-hidden">
          <div className="h-full bg-bleu transition-all" style={{ width: `${status.progress.percent}%` }} />
        </div>

        <div className="space-y-3">
          {steps.map((s, i) => {
            // Facultative et laissée vide (le logo) : « À compléter plus tard » avec « Compléter », plus le cadenas de
            // « Complétez l'étape précédente d'abord » (recette du 15/10 ; décision de Mélilia, question 16).
            const skipped = !s.done && !s.required && i < Math.max(activeIndex, furthest);
            const state = i === activeIndex ? "active" : s.done ? "completed" : skipped ? "skipped" : "locked";
            return (
              <OnboardingCard key={s.key} title={s.title} description={s.description} icon={s.icon}
                state={state} required={s.required} testId={`onboarding-card-${s.key}`}
                sub={s.done ? "Complété" : null}
                onEdit={(s.done || skipped) && i !== activeIndex ? () => goTo(i) : null}>
                {s.key === "company" && <CompanyStep data={formData.company} onChange={(d) => setFormData((f) => ({ ...f, company: d }))} firstFieldRef={i === activeIndex ? firstFieldRef : undefined} />}
                {s.key === "address" && <AddressStep data={formData.address} onChange={(d) => setFormData((f) => ({ ...f, address: d }))} firstFieldRef={i === activeIndex ? firstFieldRef : undefined} />}
                {s.key === "identity" && <IdentityStep logos={formData.identity.logos} onUpload={uploadLogo} onDelete={deleteLogo} uploading={uploadingLogo} firstFieldRef={i === activeIndex ? firstFieldRef : undefined} />}
                {s.key === "contact" && <ContactStep data={formData.contact} onChange={(d) => setFormData((f) => ({ ...f, contact: d }))} firstFieldRef={i === activeIndex ? firstFieldRef : undefined} />}
                {s.key === "preferences" && <PreferencesStep data={formData.preferences} onChange={(d) => setFormData((f) => ({ ...f, preferences: d }))} firstFieldRef={i === activeIndex ? firstFieldRef : undefined} />}
                <div className="flex justify-end pt-1">
                  <button type="button" onClick={saveAndAdvance} disabled={saving} data-testid="onboarding-next"
                    className="bg-marine text-white rounded-sm px-5 py-2 text-sm hover:bg-marine-hover active:scale-95 transition-all disabled:opacity-50 flex items-center gap-2">
                    {saving && <Loader2 size={16} className="animate-spin" />}
                    {isLast ? "Terminer" : "Suivant"}
                  </button>
                </div>
              </OnboardingCard>
            );
          })}
        </div>

        {agencyContact?.email && (
          <p className="text-xs text-marine/80 mt-6 text-center">
            Une question ? Contactez l'agence —{" "}
            <a href={`mailto:${agencyContact.email}`} data-testid="onboarding-agency-hint" className="text-marine underline underline-offset-2">
              {agencyContact.email}
            </a>
          </p>
        )}
      </div>
    </div>
  );
}
